// @djsem/shared — Auth: accounts, organisations, teams, roles, permissions (Prompt 28).
//
// This module owns the DATA MODEL and RBAC POLICY for auth. It does not perform password
// hashing, session storage, or Cloudflare Access token verification — those already live in
// apps/analysis-worker/src/auth. This module makes those primitives typed and reusable so
// the same permission model applies in the worker, Electron shell, and any future runtime.
//
// Boundary: hard rule — never allow client (renderer/browser) direct database access. Auth
// enforcement happens at the worker route boundary; this module supplies the vocabulary.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "./validation";
import { SeqCounter } from "./runtime";

// ---- role model -------------------------------------------------------------

/** A user's role within an organisation. `custom` opts into an explicit `customPermissions` list. */
export type DJSEMOrganisationRole = "owner" | "admin" | "manager" | "contributor" | "viewer" | "guest" | "custom";
/** A user's role within a team (scoped beneath an organisation). */
export type DJSEMTeamRole = "team_admin" | "team_member" | "team_viewer";
/** A user's role within a project. */
export type DJSEMProjectRole = "project_lead" | "project_contributor" | "project_reviewer" | "project_viewer";
/** A single granular permission that can be granted via a role or custom permission list. */
export type DJSEMSystemPermission =
  // Reads
  | "read_project" | "read_library" | "read_show_pack" | "read_approved_snapshot" | "read_billing"
  // Writes
  | "write_library" | "write_project" | "write_brand_world" | "write_visual_scene"
  | "write_set" | "write_release" | "write_asset"
  // Approvals
  | "approve_snapshot" | "approve_release" | "approve_marketplace_pack"
  // Runs
  | "run_analysis" | "run_ai_generation" | "run_render"
  // Manage
  | "invite_user" | "manage_billing" | "manage_org_settings"
  // System
  | "impersonate_user" | "admin_all";

/**
 * Baseline role → permissions. Applied per-scope (org / team / project) via inheritsFrom
 * on custom roles. Enforces "principle of least privilege" defaults.
 */
export const BASELINE_ROLE_PERMISSIONS: Record<Exclude<DJSEMOrganisationRole, "custom">, DJSEMSystemPermission[]> = {
  owner: ["admin_all"],
  admin: ["read_project", "read_library", "read_show_pack", "read_approved_snapshot", "read_billing", "write_library", "write_project", "write_brand_world", "write_visual_scene", "write_set", "write_release", "write_asset", "approve_snapshot", "approve_release", "approve_marketplace_pack", "run_analysis", "run_ai_generation", "run_render", "invite_user", "manage_billing", "manage_org_settings"],
  manager: ["read_project", "read_library", "read_show_pack", "read_approved_snapshot", "write_library", "write_project", "write_brand_world", "write_visual_scene", "write_set", "write_release", "write_asset", "approve_snapshot", "approve_release", "run_analysis", "run_ai_generation", "run_render", "invite_user"],
  contributor: ["read_project", "read_library", "read_show_pack", "read_approved_snapshot", "write_library", "write_brand_world", "write_visual_scene", "write_set", "write_asset", "run_analysis", "run_ai_generation"],
  viewer: ["read_project", "read_library", "read_show_pack", "read_approved_snapshot"],
  guest: ["read_project"],
};

// Privilege ranking, highest first. Used to stop a member from granting a role more
// privileged than their own when adding/inviting another member (privilege escalation).
export const ORGANISATION_ROLE_RANK: Record<Exclude<DJSEMOrganisationRole, "custom">, number> = {
  owner: 5, admin: 4, manager: 3, contributor: 2, viewer: 1, guest: 0,
};

/** True if `granterRole` is privileged enough to assign `targetRole` to someone else. A role
 * can never grant a role more privileged than its own; `owner` can never be self-service-granted
 * by anyone but an existing owner. */
export function canGrantOrganisationRole(
  granterRole: Exclude<DJSEMOrganisationRole, "custom">,
  targetRole: Exclude<DJSEMOrganisationRole, "custom">,
): boolean {
  return ORGANISATION_ROLE_RANK[granterRole] >= ORGANISATION_ROLE_RANK[targetRole];
}

// ---- contracts --------------------------------------------------------------

/** A DJSEM user account. Never carries password material — the worker's auth module owns hashes. */
export interface DJSEMUserAccount {
  id: string;
  email: string;
  displayName?: string;
  createdAt: string;
  disabled: boolean;
  primaryOrganisationId?: string;
  /** Never store password material here — worker's auth module owns hashes. */
}

/** An organisation (the top-level billing/ownership scope). */
export interface DJSEMOrganisation {
  id: string;
  name: string;
  ownerId: string;
  planLevel: string;
  createdAt: string;
  disabled: boolean;
}

/** A user's membership in an organisation, with role and optional custom permission overrides. */
export interface DJSEMOrganisationMembership {
  id: string;
  userId: string;
  organisationId: string;
  role: DJSEMOrganisationRole;
  customPermissions?: DJSEMSystemPermission[];
  addedBy?: string;
  addedAt: string;
}

/** A team scoped beneath an organisation. */
export interface DJSEMTeam {
  id: string;
  organisationId: string;
  name: string;
  createdAt: string;
  memberUserIds: string[];
}

/** A user's membership in a team, with a team-scoped role. */
export interface DJSEMTeamMembership {
  id: string;
  teamId: string;
  userId: string;
  role: DJSEMTeamRole;
  addedAt: string;
}

/** A user's membership in a project, with a project-scoped role. */
export interface DJSEMProjectMembership {
  id: string;
  projectId: string;
  userId: string;
  role: DJSEMProjectRole;
  addedAt: string;
}

/** A pending (or resolved) invitation for an email address to join an organisation with a given role. */
export interface DJSEMInvitation {
  id: string;
  organisationId: string;
  invitedEmail: string;
  role: DJSEMOrganisationRole;
  status: "pending" | "accepted" | "revoked" | "expired";
  createdBy: string;
  createdAt: string;
  expiresAt: string;
  acceptedAt?: string;
  /** Token is stored as HASH ONLY; the plaintext is emailed once and never retrievable. */
  tokenHash: string;
}

/** The outcome of an authorisation check: whether access was granted, and why. */
export interface DJSEMAuthDecision {
  granted: boolean;
  reason: string;
  scope?: "org" | "team" | "project";
  scopeId?: string;
}

// ---- inputs -----------------------------------------------------------------

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();
function id(prefix: string): string { return `${prefix}_${_seq.next()}`; }

/** Input to {@link DJSEMAccountService.createUserAccount}. */
export interface CreateUserAccountInput { id?: string; email: string; displayName?: string; primaryOrganisationId?: string; createdAt?: string; }
/** Input to {@link DJSEMOrganisationService.createOrganisation}. */
export interface CreateOrganisationInput { id?: string; name: string; ownerId: string; planLevel?: string; createdAt?: string; }
/** Input to {@link DJSEMOrganisationService.createOrganisationMembership}. */
export interface CreateOrganisationMembershipInput { id?: string; userId: string; organisationId: string; role: DJSEMOrganisationRole; customPermissions?: DJSEMSystemPermission[]; addedBy?: string; addedAt?: string; }
/** Input to {@link DJSEMTeamService.createTeam}. */
export interface CreateTeamInput { id?: string; organisationId: string; name: string; createdAt?: string; }
/** Input to {@link DJSEMTeamService.createTeamMembership}. */
export interface CreateTeamMembershipInput { id?: string; teamId: string; userId: string; role: DJSEMTeamRole; addedAt?: string; }
/** Input to {@link DJSEMProjectMembershipService.createProjectMembership}. */
export interface CreateProjectMembershipInput { id?: string; projectId: string; userId: string; role: DJSEMProjectRole; addedAt?: string; }
/** Input to {@link DJSEMInvitationService.createInvitation}. */
export interface CreateInvitationInput { id?: string; organisationId: string; invitedEmail: string; role: DJSEMOrganisationRole; createdBy: string; createdAt?: string; ttlDays?: number; tokenHash: string; }

/** Input to {@link DJSEMAuthorisationService.authorise}. */
export interface AuthorisationInput {
  userId: string;
  requiredPermission: DJSEMSystemPermission;
  organisationMemberships: DJSEMOrganisationMembership[];
  teamMemberships?: DJSEMTeamMembership[];
  projectMemberships?: DJSEMProjectMembership[];
  targetOrganisationId?: string;
  targetTeamId?: string;
  targetProjectId?: string;
}

/** Input to {@link DJSEMAuthorisationService.effectivePermissions}. */
export interface EffectivePermissionInput {
  userId: string;
  organisationMemberships: DJSEMOrganisationMembership[];
  targetOrganisationId: string;
}

// ---- account service --------------------------------------------------------

/** Creates and validates {@link DJSEMUserAccount} records. */
export class DJSEMAccountService {
  /**
   * Creates a new user account, normalizing the email to lowercase/trimmed.
   * @param input - The account fields to create.
   * @returns The new, non-disabled user account.
   */
  createUserAccount(input: CreateUserAccountInput): DJSEMUserAccount {
    return {
      id: input.id ?? id("user"),
      email: input.email.toLowerCase().trim(),
      displayName: input.displayName,
      createdAt: input.createdAt ?? AT,
      disabled: false,
      primaryOrganisationId: input.primaryOrganisationId,
    };
  }

  /**
   * Validates a user account's shape (required id, valid + lowercase email).
   * @param u - The account to validate.
   * @returns The validation result.
   */
  validateUserAccount(u: DJSEMUserAccount): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!u.id) issues.push({ path: "$.id", message: "required" });
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u.email)) issues.push({ path: "$.email", message: "invalid email" });
    if (u.email !== u.email.toLowerCase()) issues.push({ path: "$.email", message: "must be lowercase" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "user_account");
  }
}

// ---- organisation service ---------------------------------------------------

/** Creates organisations and organisation memberships, and enforces the "at least one owner" guardrail. */
export class DJSEMOrganisationService {
  /**
   * Creates a new organisation.
   * @param input - The organisation fields to create.
   * @returns The new, non-disabled organisation.
   */
  createOrganisation(input: CreateOrganisationInput): DJSEMOrganisation {
    return {
      id: input.id ?? id("org"),
      name: input.name, ownerId: input.ownerId,
      planLevel: input.planLevel ?? "free_starter",
      createdAt: input.createdAt ?? AT, disabled: false,
    };
  }

  /**
   * Creates a new organisation membership.
   * @param input - The membership fields to create.
   * @returns The new membership record.
   */
  createOrganisationMembership(input: CreateOrganisationMembershipInput): DJSEMOrganisationMembership {
    return {
      id: input.id ?? id("orgmem"),
      userId: input.userId, organisationId: input.organisationId,
      role: input.role, customPermissions: input.customPermissions,
      addedBy: input.addedBy, addedAt: input.addedAt ?? AT,
    };
  }

  /**
   * Guardrail: an org must always keep at least one owner. Blocks a change that would
   * remove or demote the last remaining owner (never accidentally lock everyone out).
   * @param input - The full membership list, which membership is changing, and the new
   *   role and/or removal flag being applied.
   * @returns The validation result (invalid if the change would leave the org ownerless).
   */
  validateMembershipChange(input: {
    memberships: DJSEMOrganisationMembership[];
    changingMembershipId: string;
    newRole?: DJSEMOrganisationRole;
    removing?: boolean;
  }): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    const m = input.memberships.find((x) => x.id === input.changingMembershipId);
    if (!m) { issues.push({ path: "$.changingMembershipId", message: "membership not found" }); return toDJSEMValidationResult({ valid: false, issues }, "membership_change"); }
    if (m.role === "owner") {
      const owners = input.memberships.filter((x) => x.role === "owner" && x.organisationId === m.organisationId);
      const becomingNonOwner = input.removing || (input.newRole && input.newRole !== "owner");
      if (owners.length <= 1 && becomingNonOwner) {
        issues.push({ path: "$", message: "organisation must retain at least one owner" });
      }
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "membership_change");
  }
}

// ---- team service -----------------------------------------------------------

/** Creates teams and manages their membership lists. */
export class DJSEMTeamService {
  /**
   * Creates a new, empty team.
   * @param input - The team fields to create.
   * @returns The new team with no members.
   */
  createTeam(input: CreateTeamInput): DJSEMTeam {
    return { id: input.id ?? id("team"), organisationId: input.organisationId, name: input.name, createdAt: input.createdAt ?? AT, memberUserIds: [] };
  }
  /**
   * Creates a new team membership record.
   * @param input - The membership fields to create.
   * @returns The new membership record.
   */
  createTeamMembership(input: CreateTeamMembershipInput): DJSEMTeamMembership {
    return { id: input.id ?? id("tmem"), teamId: input.teamId, userId: input.userId, role: input.role, addedAt: input.addedAt ?? AT };
  }
  /**
   * Adds a user to a team's member list (idempotent — no duplicate entries).
   * @param team - The team to update.
   * @param userId - The user to add.
   * @returns A new team with the user added (or the same team if already a member).
   */
  addMemberToTeam(team: DJSEMTeam, userId: string): DJSEMTeam {
    if (team.memberUserIds.includes(userId)) return team;
    return { ...team, memberUserIds: [...team.memberUserIds, userId] };
  }
  /**
   * Removes a user from a team's member list.
   * @param team - The team to update.
   * @param userId - The user to remove.
   * @returns A new team without the given user.
   */
  removeMemberFromTeam(team: DJSEMTeam, userId: string): DJSEMTeam {
    return { ...team, memberUserIds: team.memberUserIds.filter((x) => x !== userId) };
  }
}

// ---- project membership service ---------------------------------------------

/** Creates project membership records. */
export class DJSEMProjectMembershipService {
  /**
   * Creates a new project membership record.
   * @param input - The membership fields to create.
   * @returns The new membership record.
   */
  createProjectMembership(input: CreateProjectMembershipInput): DJSEMProjectMembership {
    return { id: input.id ?? id("pmem"), projectId: input.projectId, userId: input.userId, role: input.role, addedAt: input.addedAt ?? AT };
  }
}

// ---- invitation service -----------------------------------------------------

/** Creates organisation invitations and verifies invitation tokens by hash comparison only. */
export class DJSEMInvitationService {
  /**
   * Creates a pending invitation for an email address to join an organisation with a role.
   * @param input - The invitation fields, including the (already-hashed) invitation token
   *   and optional `ttlDays` (defaults to 14).
   * @returns The new, pending invitation.
   * @throws If `tokenHash` is missing or shorter than 32 characters.
   */
  createInvitation(input: CreateInvitationInput): DJSEMInvitation {
    if (!input.tokenHash || input.tokenHash.length < 32) throw new Error("Invitation token hash missing or too short.");
    const ttlDays = input.ttlDays ?? 14;
    const createdAt = input.createdAt ?? AT;
    const expiresAt = new Date(Date.parse(createdAt) + ttlDays * 24 * 60 * 60 * 1000).toISOString();
    return {
      id: input.id ?? id("inv"),
      organisationId: input.organisationId,
      invitedEmail: input.invitedEmail.toLowerCase().trim(),
      role: input.role, status: "pending",
      createdBy: input.createdBy, createdAt,
      expiresAt, tokenHash: input.tokenHash,
    };
  }

  /**
   * Verifies a provided invitation token by comparing hashes only (constant-time), and
   * checks the invitation's status/expiry. The plaintext token never enters this module.
   * @param invite - The invitation to verify against.
   * @param providedTokenHash - The hash of the token the invitee provided.
   * @param now - Current ISO timestamp, used to check expiry.
   * @returns The validation result (invalid if not pending, expired, or the hash mismatches).
   */
  verifyInvitationToken(invite: DJSEMInvitation, providedTokenHash: string, now: string): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (invite.status !== "pending") issues.push({ path: "$.status", message: `invitation ${invite.status}` });
    if (Date.parse(invite.expiresAt) < Date.parse(now)) issues.push({ path: "$.expiresAt", message: "invitation expired" });
    // Constant-time comparison — same length prevents timing side channel.
    if (invite.tokenHash.length !== providedTokenHash.length) {
      issues.push({ path: "$.tokenHash", message: "invalid token" });
    } else {
      let diff = 0;
      for (let i = 0; i < invite.tokenHash.length; i++) diff |= invite.tokenHash.charCodeAt(i) ^ providedTokenHash.charCodeAt(i);
      if (diff !== 0) issues.push({ path: "$.tokenHash", message: "invalid token" });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "invitation_verify");
  }
}

// ---- authorisation service --------------------------------------------------

/** Computes effective permissions and authorisation decisions for org-scoped requests. */
export class DJSEMAuthorisationService {
  /**
   * Computes the effective permission set for a user in a given org: baseline role
   * permissions expanded, plus any custom permissions unioned in. 'owner' role (or a custom
   * role granting `admin_all`) is short-circuited to `["admin_all"]`.
   * @param input - The user, their organisation memberships, and the target organisation.
   * @returns The effective permission list (empty if the user has no membership in that org).
   */
  effectivePermissions(input: EffectivePermissionInput): DJSEMSystemPermission[] {
    const membership = input.organisationMemberships.find((m) => m.userId === input.userId && m.organisationId === input.targetOrganisationId);
    if (!membership) return [];
    if (membership.role === "owner" || membership.role === "custom" && membership.customPermissions?.includes("admin_all")) {
      return ["admin_all"];
    }
    const base = membership.role === "custom" ? [] : BASELINE_ROLE_PERMISSIONS[membership.role] ?? [];
    const custom = membership.customPermissions ?? [];
    return [...new Set([...base, ...custom])];
  }

  /**
   * Authorises a request for a required permission within an organisation context. Every
   * request must have an org context — permissions are always org-scoped.
   * @param input - The user, required permission, memberships, and target org/team/project.
   * @returns The authorisation decision (granted or denied, with a human-readable reason).
   */
  authorise(input: AuthorisationInput): DJSEMAuthDecision {
    // Every request must have an org context — permissions are always org-scoped.
    if (!input.targetOrganisationId) return { granted: false, reason: "No target organisation." };
    const perms = this.effectivePermissions({
      userId: input.userId,
      organisationMemberships: input.organisationMemberships,
      targetOrganisationId: input.targetOrganisationId,
    });
    if (perms.includes("admin_all")) return { granted: true, reason: "admin_all", scope: "org", scopeId: input.targetOrganisationId };
    if (perms.includes(input.requiredPermission)) return { granted: true, reason: `org role grants ${input.requiredPermission}`, scope: "org", scopeId: input.targetOrganisationId };
    return { granted: false, reason: `Missing '${input.requiredPermission}' in organisation.`, scope: "org", scopeId: input.targetOrganisationId };
  }
}
